import type { CrossSectionDataSet, MotorDataContext } from "./d";
import {
  generateEquipmentDataPoints,
  generateMLEDataPoints,
  generateCrossSectionDataSetTitle
} from "./calculateCrossSectionDataSets";
import { convertSmallLengthUnits } from "@/libs/units/length";
import { getYToolBypassPositioning } from "./getYToolChartDataSet";

/**
 * Generates a dataset representing the motor cross-section with multiple elements
 *
 * @param context The cross-section chart context
 * @returns A dataset representing the detailed motor cross-section
 */
export const getMotorChartDataSet = (
  motorDataContext: MotorDataContext
): CrossSectionDataSet[] => {
  const motorChartDataSet: CrossSectionDataSet[] = [];
  // Radius =$Inputs.F4/2, Center X, Center Y
  const casingInnerRadius = motorDataContext.casing.innerDiameterIn / 2;
  const casingInnerCenterX = 0;
  const casingInnerCenterY = 0;
  const casingInnerPoints = generateEquipmentDataPoints(
    casingInnerRadius,
    casingInnerCenterX,
    casingInnerCenterY
  );
  const casingDriftRadius = motorDataContext.casing.driftDiameterIn / 2;
  const casingDriftCenterX = 0;
  const casingDriftCenterY = 0;
  const casingDriftPoints = generateEquipmentDataPoints(
    casingDriftRadius,
    casingDriftCenterX,
    casingDriftCenterY
  );
  motorChartDataSet.push({
    title: generateCrossSectionDataSetTitle(
      "Casing ID",
      motorDataContext.casing.innerDiameterIn,
      motorDataContext.lengthUnit
    ),
    xAxisLabel: `${motorDataContext.lengthUnit.label}`,
    yAxisLabel: `${motorDataContext.lengthUnit.label}`,
    points: casingInnerPoints.map(pt => {
      return {
        x: convertSmallLengthUnits(pt.x, motorDataContext.lengthUnit),
        y: convertSmallLengthUnits(pt.y, motorDataContext.lengthUnit)
      };
    })
  });
  motorChartDataSet.push({
    title: generateCrossSectionDataSetTitle(
      "Casing Drift ID",
      motorDataContext.casing.driftDiameterIn,
      motorDataContext.lengthUnit
    ),
    xAxisLabel: `${motorDataContext.lengthUnit.label}`,
    yAxisLabel: `${motorDataContext.lengthUnit.label}`,
    points: casingDriftPoints.map(pt => {
      return {
        x: convertSmallLengthUnits(pt.x, motorDataContext.lengthUnit),
        y: convertSmallLengthUnits(pt.y, motorDataContext.lengthUnit)
      };
    })
  });
  // Motor Dimension
  const motorDimRadius = motorDataContext?.outerDiameterIn / 2;
  const maxEquipmentOuterDiameterIn =
    motorDataContext.maxEquipmentOuterDiameterIn || 0;
  let motorDimCenterX: number;
  const motorDimCenterY = 0;

  if (
    motorDataContext.hasYTool &&
    motorDataContext.bypassTubingOuterDiameterIn &&
    motorDataContext.yToolWidthIn
  ) {
    // Y-Tool Bypass Tubing Dim circle - use shared positioning logic
    const bypassPositioning = getYToolBypassPositioning({
      casingDriftRadius,
      yToolWidthIn: motorDataContext.yToolWidthIn,
      bypassTubingOuterDiameterIn: motorDataContext.bypassTubingOuterDiameterIn,
      lengthUnit: motorDataContext.lengthUnit
    });
    motorChartDataSet.push(bypassPositioning.bypassTubingDataSet);

    // Position motor using Excel formula: ='Y-Tool Cross'!K4
    // Max Equipment Center X = bypassCenterX + bypassRadius + maxEquipmentRadius
    const maxEquipmentDimRadius = maxEquipmentOuterDiameterIn / 2;
    motorDimCenterX =
      bypassPositioning.bypassTubingDimCenterX +
      bypassPositioning.bypassTubingDimRadius +
      maxEquipmentDimRadius;
  } else {
    // Standard positioning when no y-tool
    motorDimCenterX = -casingDriftRadius + maxEquipmentOuterDiameterIn / 2;
  }

  const motorDimPoints = generateEquipmentDataPoints(
    motorDimRadius,
    motorDimCenterX,
    motorDimCenterY
  );
  motorChartDataSet.push({
    title: generateCrossSectionDataSetTitle(
      "Motor OD",
      motorDataContext.outerDiameterIn,
      motorDataContext.lengthUnit
    ),
    xAxisLabel: `${motorDataContext.lengthUnit.label}`,
    yAxisLabel: `${motorDataContext.lengthUnit.label}`,
    points: motorDimPoints.map(pt => {
      return {
        x: convertSmallLengthUnits(pt.x, motorDataContext.lengthUnit),
        y: convertSmallLengthUnits(pt.y, motorDataContext.lengthUnit)
      };
    })
  });
  // Only chart MLE and Motor + Rub OD when there's no y-tool
  if (!motorDataContext.hasYTool) {
    // "MLE Upper Dimension"
    const mleUpperDimRadius = motorDataContext?.mle.depthIn / 2;
    const mleUpperDimCenterX =
      motorDimRadius -
      motorDataContext.mleOffsetDepthIn +
      motorDimCenterX +
      mleUpperDimRadius;
    const mleUpperDimCenterY =
      motorDataContext?.mle.widthIn / 2 - motorDataContext?.mle.depthIn / 2;

    // "MLE Lower Dimension"
    const mleLowerDimRadius = mleUpperDimRadius;
    const mleLowerDimCenterX = mleUpperDimCenterX;
    const mleLowerDimCenterY = -mleUpperDimCenterY;

    const mleDimPoints = generateMLEDataPoints(
      mleUpperDimRadius,
      mleUpperDimCenterX,
      mleUpperDimCenterY,
      mleLowerDimRadius,
      mleLowerDimCenterX,
      mleLowerDimCenterY
    );
    motorChartDataSet.push({
      title: generateCrossSectionDataSetTitle(
        "MLE Depth",
        motorDataContext.mle.depthIn,
        motorDataContext.lengthUnit
      ),
      xAxisLabel: `${motorDataContext.lengthUnit.label}`,
      yAxisLabel: `${motorDataContext.lengthUnit.label}`,
      points: mleDimPoints.map(pt => {
        return {
          x: convertSmallLengthUnits(pt.x, motorDataContext.lengthUnit),
          y: convertSmallLengthUnits(pt.y, motorDataContext.lengthUnit)
        };
      })
    });
    // Calculate rubs and motor + rub dimensions
    // If they are equal we won't produce another dataset
    if (
      motorDataContext.outerDiameterIn !==
      motorDataContext.maxCalculatedOuterDiameterIn
    ) {
      // "Motor + Rub Dimension"
      const motorAndRubRadius =
        motorDataContext?.maxCalculatedOuterDiameterIn / 2;
      const motorAndRubCenterX =
        motorAndRubRadius - motorDimRadius + motorDimCenterX; // Needs to be =Q3-G3+G4
      const motorAndRubCenterY = 0;
      const motorAndRubPoints = generateEquipmentDataPoints(
        motorAndRubRadius,
        motorAndRubCenterX,
        motorAndRubCenterY
      );
      motorChartDataSet.push({
        title: generateCrossSectionDataSetTitle(
          "Motor + Rub OD",
          motorDataContext?.maxCalculatedOuterDiameterIn,
          motorDataContext.lengthUnit
        ),
        xAxisLabel: `${motorDataContext.lengthUnit.label}`,
        yAxisLabel: `${motorDataContext.lengthUnit.label}`,
        points: motorAndRubPoints.map(pt => {
          return {
            x: convertSmallLengthUnits(pt.x, motorDataContext.lengthUnit),
            y: convertSmallLengthUnits(pt.y, motorDataContext.lengthUnit)
          };
        })
      });
      // We will not be charting these unless Ben gets dimensions needed
      // for calculations
      // motorChartDataSet.push({
      //   title: "Motor Upper Rub Dim",
      //   xAxisLabel: `${motorDataContext.lengthUnit.label}`,
      //   yAxisLabel: `${motorDataContext.lengthUnit.label}`,
      //   points: rubUpperDimPoints.map(pt => {
      //     return {
      //       x: convertSmallLengthUnits(pt.x, motorDataContext.lengthUnit),
      //       y: convertSmallLengthUnits(pt.y, motorDataContext.lengthUnit)
      //     };
      //   })
      // });
      // motorChartDataSet.push({
      //   title: "Motor Lower Rub Dim",
      //   xAxisLabel: `${motorDataContext.lengthUnit.label}`,
      //   yAxisLabel: `${motorDataContext.lengthUnit.label}`,
      //   points: rubLowerDimPoints.map(pt => {
      //     return {
      //       x: convertSmallLengthUnits(pt.x, motorDataContext.lengthUnit),
      //       y: convertSmallLengthUnits(pt.y, motorDataContext.lengthUnit)
      //     };
      //   })
      // });
    }
  }
  return motorChartDataSet;
};
